import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';

const API_URL = process.env.REACT_APP_API_URL;

const UnitList = () => {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ show: false, unitId: null, unitName: '' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  useEffect(() => {
    const fetchUnits = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}add-unit-list.php`);
        const data = await response.json();
        if (data.units) {
          setUnits(data.units);
        } else {
          setUnits([]);
        }
      } catch (error) {
        showToast('Failed to fetch units', 'error');
        setUnits([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUnits();
  }, []);

  const filteredUnits = units.filter(unit => {
    const matchesSearch = unit.unitname.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleEdit = (unit) => {
    navigate(`/edit-unit/${unit.id}`, { state: { unit } });
    showToast(`Editing unit: ${unit.unitname}`, 'info');
  };

  const handleDelete = (unitId) => {
    const unitToDelete = units.find(unit => unit.id === unitId);
    setDeleteModal({ show: true, unitId, unitName: unitToDelete.unitname });
  };

  const confirmDelete = async () => {
    try {
      const formData = new FormData();
      formData.append('id', deleteModal.unitId);

      const response = await fetch(`${API_URL}delete-unit.php`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.message && data.message.includes('successfully')) {
        setUnits(units.filter(unit => unit.id !== deleteModal.unitId));
        showToast(data.message, 'success');
      } else {
        showToast(data.message || 'Failed to delete unit', 'error');
      }
    } catch (error) {
      console.error('Error deleting unit:', error);
      showToast('Failed to delete unit. Please try again.', 'error');
    } finally {
      setDeleteModal({ show: false, unitId: null, unitName: '' });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, unitId: null, unitName: '' });
  };

  const handleAddUnit = () => {
    navigate('/add-unit');
    showToast('Navigating to add new unit', 'info');
  };

  return (
    <div className="unit-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Unit Management</h1>
          <p>Manage measurement units</p>
        </div>
        <button className="add-unit-button" onClick={handleAddUnit}>
          + Add New Unit
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search units by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Unit Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={2}>Loading units...</td></tr>
            ) : filteredUnits.length === 0 ? (
              <tr><td colSpan={2}>No units found.</td></tr>
            ) : (
              filteredUnits.map(unit => (
                <tr key={unit.id}>
                  <td>
                    <div className="unit-info">
                      <div className="unit-icon">
                        📏
                      </div>
                      <span>{unit.unitname}</span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEdit(unit)}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDelete(unit.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredUnits.length} of {units.length} units
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the unit "<strong>{deleteModal.unitName}</strong>"?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={cancelDelete}>
                No, Cancel
              </button>
              <button className="btn-confirm" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitList; 